/**
 * Connection Status Schema (Effect)
 *
 * Effect-TS schemas for connectivity monitoring of all required systems
 * including API connectivity, Telematik infrastructure, and card reader status.
 *
 */

import { Schema as S } from "effect";

/**
 * Connection status for the check-in kiosk.
 *
 * Monitors all required connections:
 * - Bridge API reachability
 * - Telematik/Gematik connector availability
 * - Card reader terminal detection
 * - Medical professional card presence (SMC-B or HBA)
 *
 * Used for pre-flight checks before enabling check-in flow.
 * Updated every 30 seconds during standby mode.
 */
export const ConnectionStatusSchema = S.Struct({
  /**
   * Whether the bridge API is reachable.
   * Must be true for any PMS operations.
   */
  isConnectedToAPI: S.Boolean,

  /**
   * Whether the Telematik infrastructure (Gematik connector) is available.
   * Required for card reading operations.
   */
  telematikIsAvailable: S.Boolean,

  /**
   * Whether the selected card reader terminal is detected and connected.
   * Required for card reading operations.
   */
  selectedCardReaderIsAvailable: S.Boolean,

  /**
   * Whether a medical professional card (SMC-B or HBA) is inserted.
   * Required for authenticating card read operations.
   */
  hasMedicalCard: S.Boolean,
});

export type ConnectionStatus = S.Schema.Type<typeof ConnectionStatusSchema>;

/**
 * Type guard to check if system is fully operational.
 * All connection flags must be true.
 */
export const isSystemReady = (status: ConnectionStatus): boolean => {
  return (
    status.isConnectedToAPI &&
    status.telematikIsAvailable &&
    status.selectedCardReaderIsAvailable &&
    status.hasMedicalCard
  );
};

/**
 * Human-readable connection status messages.
 */
export const getConnectionStatusMessage = (
  status: ConnectionStatus
): string[] => {
  const messages: string[] = [];

  if (!status.isConnectedToAPI) {
    messages.push("Bridge API is nicht erreichbar");
  }
  if (!status.telematikIsAvailable) {
    messages.push("Telematik-Infrastruktur ist nicht verfügbar");
  }
  if (!status.selectedCardReaderIsAvailable) {
    messages.push("Kartenleser ist nicht verbunden");
  }
  if (!status.hasMedicalCard) {
    messages.push("SMC-B oder HBA Karte fehlt");
  }

  if (messages.length === 0) {
    messages.push("Alle Systeme betriebsbereit");
  }

  return messages;
};
